import {
    Button,
    Card,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Text,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, CheckmarkCircleFilled, DocumentRegular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClientCreationResponse, ClientsApiFunctionKeys } from '../../api';
import { ClientOnboardingFormAtom, DefaultClientOnboardingForm } from '../../store/ClientOnboardingFormAtom';
import { filterEnabledSystems } from '../../utils/features';
import Container from '../layout/Container';

const SYSTEM_LABELS: Record<string, string> = {
    CCH: 'CCH Axcess',
    MPM: 'ModernPM',
};

const useStyles = makeStyles({
    panel: {
        maxWidth: '520px',
        margin: '60px auto 0',
        textAlign: 'center',
        padding: '40px 36px',
    },
    check: {
        width: '64px',
        height: '64px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: '#e6f4ea',
        color: '#2c8a4f',
        display: 'grid',
        placeItems: 'center',
        margin: '0 auto 20px',
        fontSize: '34px',
    },
    title: {
        fontSize: '22px',
        fontWeight: tokens.fontWeightBold,
        display: 'block',
        marginBottom: '6px',
    },
    sub: {
        color: tokens.colorNeutralForeground2,
        fontSize: '14.5px',
        display: 'block',
    },
    idChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        margin: '14px 0 6px',
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: '16px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
        border: `1px solid ${tokens.colorBrandStroke2}`,
        padding: '9px 16px',
        borderRadius: tokens.borderRadiusMedium,
    },
    sysLine: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        margin: '16px 0 24px',
        flexWrap: 'wrap',
    },
    sysPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusCircular,
        padding: '6px 12px',
    },
    sysDot: {
        width: '7px',
        height: '7px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: '#41995c',
    },
    warnings: {
        marginTop: '18px',
        textAlign: 'left',
    },
    warningList: {
        margin: '6px 0 0',
        paddingLeft: '18px',
    },
    actions: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
});

const ClientSuccess = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [clientOnboardingForm, setClientOnboardingForm] = useAtom(ClientOnboardingFormAtom);
    const creationResponse = location.state.client as ClientCreationResponse;
    const systems = filterEnabledSystems(clientOnboardingForm.systems);
    const warnings = creationResponse.warnings ?? [];

    // Prefer the CCH client ID; fall back to the GUID for ModernPM-only creates.
    const displayId = (systems.includes('CCH') && creationResponse.clientId) || creationResponse.id;

    const returnToClientList = () => {
        setClientOnboardingForm(DefaultClientOnboardingForm);
        queryClient.invalidateQueries({ queryKey: [ClientsApiFunctionKeys.clientsGet], exact: false });
        navigate('/Recent');
    };

    const createAnother = () => {
        setClientOnboardingForm(DefaultClientOnboardingForm);
        navigate('/FormPage');
    };

    return (
        <Container>
            <Card className={styles.panel}>
                <div className={styles.check} aria-hidden>
                    <CheckmarkCircleFilled />
                </div>
                <Text className={styles.title}>Client created</Text>
                <Text className={styles.sub}>
                    <strong>{creationResponse.name}</strong> has been successfully created.
                </Text>

                {warnings.length > 0 && (
                    <MessageBar intent="warning" layout="multiline" className={styles.warnings}>
                        <MessageBarBody>
                            <MessageBarTitle>Created, but not everything succeeded</MessageBarTitle>
                            The client exists. Finish the items below in the system named; don&apos;t submit the form
                            again or the client will be duplicated.
                            <ul className={styles.warningList}>
                                {warnings.map((w) => (
                                    <li key={w}>{w}</li>
                                ))}
                            </ul>
                        </MessageBarBody>
                    </MessageBar>
                )}

                {displayId && (
                    <div className={styles.idChip}>
                        <DocumentRegular />
                        {displayId}
                    </div>
                )}

                <div className={styles.sysLine}>
                    {systems.map((s) => (
                        <span key={s} className={styles.sysPill}>
                            <span className={styles.sysDot} />
                            {SYSTEM_LABELS[s] ?? s}
                        </span>
                    ))}
                </div>

                <div className={styles.actions}>
                    <Button size="large" onClick={returnToClientList}>
                        Back to Directory
                    </Button>
                    <Button size="large" appearance="primary" icon={<AddRegular />} onClick={createAnother}>
                        Create Another
                    </Button>
                </div>
            </Card>
        </Container>
    );
};

export default ClientSuccess;
